import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, FileDown, Brain, HeartHandshake, Users, X, Check, MessageCircle, Activity, AudioLines } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { LeadForm } from "../components/LeadForm";
import { MovementFlow, ImpactRings } from "../components/MovementViz";
import { MotionGallery } from "../components/MotionGallery";
import { PROGRAMS } from "../data/content";
import { SCHEDULER_URL, ASSETS } from "../lib/constants";

const ACCENT = "#5b7a99";

const PATHWAY_PILLARS = [
  { title: "Conversation", desc: "When traditional verbal exchange becomes difficult.", icon: <MessageCircle size={24} /> },
  { title: "Movement", desc: "Engaging the body to bypass cognitive fatigue.", icon: <Activity size={24} /> },
  { title: "Sound", desc: "Utilizing rhythm to unlock familiar pathways.", icon: <AudioLines size={24} /> },
];

const MEMORY_WINS = ["Meaningful Memory Access", "Joyful Participation", "Sustained Observation", "Emotional Expression", "Spontaneous Connection"];

const SEATED = [
  { move: "Seated Upper-Body Extension", badge: "Range of Motion", benefit: "Improves shoulder mobility and circulation — no joint strain.", clip: "/clips/pathways-1.mp4" },
  { move: "Bilateral Drumming Patterns", badge: "Neuroplasticity", benefit: "Stimulates memory recall and cognitive activation.", clip: "/clips/pathways-2.mp4" },
  { move: "Rhythmic Breathing & Tapping", badge: "Calm & Regulation", benefit: "Eases anxiety, lowers cortisol, and invites restful states.", clip: "/clips/pathways-3.mp4" },
];

const IMPACT = [
  { label: "Cognitive Activation", level: 90, icon: <Brain size={22} />, desc: "Stimulates pattern recognition, sequencing, attention, and sustained engagement." },
  { label: "Emotional Support", level: 84, icon: <HeartHandshake size={22} />, desc: "Fosters expression, regulation, orientation, and present-moment connection." },
  { label: "Relational Connection", level: 88, icon: <Users size={22} />, desc: "Opens communication, social interaction, and shared familiar experiences." },
];

const ASSUMPTION = ["Entertainment & simple gathering", "Casual, unstructured participation", "Pressure for correct performance", "High-stimulus noise"];
const PATHWAYS = ["Structured cognitive engagement", "Predictable patterning & memory cues", "Guided play, reduced pressure to recall", "Intentional sensory & bilateral input"];

const GALLERY = [
  { video: "/clips/pathways-1.mp4", label: "Range of motion" },
  { video: "/clips/pathways-2.mp4", label: "Memory activation" },
  { video: "/clips/pathways-3.mp4", label: "Calm & regulation" },
  { image: "https://customer-assets-jt897jd0.emergentagent.net/job_trauma-healing-tech/artifacts/kqwmoohm_537097190_1076817984663968_2355432293242031302_n.webp", label: "Reconnection" },
];

export default function PercussionPathways() {
  const prog = PROGRAMS.find((p) => p.slug === "percussion-pathways");
  useEffect(() => window.scrollTo(0, 0), []);
  return (
    <div className="grain min-h-screen" data-testid="page-percussion-pathways">
      <Nav />
      <section className="relative pt-36 md:pt-48 pb-16 px-6 md:px-10 overflow-hidden">
        <div className="absolute inset-0" style={{ background: `radial-gradient(90% 70% at 20% 0%, rgba(91,122,153,0.16), transparent 60%)` }} />
        <div className="relative max-w-6xl mx-auto">
          <Link to="/#programs" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-fg/50 hover:text-fg transition-colors mb-10"><ArrowLeft size={14} /> All Programs</Link>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <img src="/pathways-logo.png" alt="Percussion Pathways" data-testid="pathways-hero-logo" className="w-24 h-24 md:w-28 md:h-28 rounded-full object-contain bg-white/90 p-1 mb-6 shadow-[0_10px_40px_rgba(91,122,153,0.28)]" />
              <p className="font-mono text-xs tracking-[0.3em] uppercase mb-4" style={{ color: ACCENT }}>For Senior Living & Memory Care</p>
              <h1 className="font-serif text-5xl md:text-6xl tracking-tight">Percussion Pathways</h1>
              <p className="mt-6 max-w-xl text-fg/70 md:text-xl leading-relaxed">Therapeutic rhythms that create meaningful moments of engagement for individuals and families navigating dementia and Alzheimer&rsquo;s — reaching memory, emotion, and connection when words no longer can.</p>
              <div className="mt-9 flex flex-wrap gap-4">
                <a href={SCHEDULER_URL} target="_blank" rel="noopener noreferrer" data-testid="pathways-book-top" className="shape-r font-medium px-7 py-3.5 transition-opacity hover:opacity-90" style={{ backgroundColor: ACCENT, color: "#141b1d" }}>Book a Session</a>
                <a href="#pathways-kit" className="shape-r border border-white/25 font-medium px-7 py-3.5 hover:bg-white/10 transition-colors">Request program details</a>
              </div>
            </div>
            <div className="shape-r overflow-hidden border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.4)]" data-testid="pathways-hero-sizzle">
              <video className="w-full aspect-[4/5] object-cover" autoPlay muted loop playsInline preload="auto">
                <source src="/clips/pathways-1.mp4" type="video/mp4" />
                <source src="/clips/pathways-1.webm" type="video/webm" />
              </video>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 px-6 md:px-10 border-t border-white/5" data-testid="pathways-pillars-section">
        <div className="max-w-5xl mx-auto">
          <p className="font-mono text-xs tracking-[0.3em] uppercase mb-3" style={{ color: ACCENT }}>Why &ldquo;Pathways&rdquo;</p>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight mb-5 max-w-3xl">Connection through more than language alone.</h2>
          <p className="text-fg/65 md:text-lg max-w-2xl mb-12 font-serif italic">
            &ldquo;The word <span style={{ color: ACCENT }}>Pathways</span> reflects the belief that connection can happen through more than language alone — sometimes it enters through conversation, movement, or sound.&rdquo;
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="pathways-pillars">
            {PATHWAY_PILLARS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.5, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                className="glass shape-r p-7 border border-white/10"
                data-testid={`pathways-pillar-${i + 1}`}
              >
                <div
                  className="grid place-items-center h-14 w-14 rounded-full mb-5"
                  style={{ backgroundColor: `${ACCENT}1F`, border: `1.5px solid ${ACCENT}`, color: ACCENT }}
                >
                  {p.icon}
                </div>
                <h3 className="font-serif text-2xl md:text-3xl mb-2.5">{p.title}</h3>
                <p className="text-fg/65 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 glass shape-r p-6 md:p-8 border border-white/10" data-testid="pathways-memory-benefits">
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase mb-4" style={{ color: ACCENT }}>What families & care teams see in memory care</p>
            <div className="flex flex-wrap gap-2.5">
              {MEMORY_WINS.map((w) => (
                <span
                  key={w}
                  className="shape-r font-mono text-[10px] md:text-[11px] tracking-[0.12em] uppercase px-3.5 py-2"
                  style={{ border: `1px solid ${ACCENT}66`, backgroundColor: `${ACCENT}14`, color: ACCENT }}
                >
                  {w}
                </span>
              ))}
            </div>
            <p className="mt-5 text-fg/70 md:text-lg leading-relaxed">
              For residents living with dementia and Alzheimer&rsquo;s, success isn&rsquo;t about correct recall — it&rsquo;s measured through participation, engagement, and expression. The ultimate goal is to <span style={{ color: ACCENT }}>honor dignity and ability</span> in every session.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 px-6 md:px-10 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <p className="font-mono text-xs tracking-[0.3em] uppercase mb-3" style={{ color: ACCENT }}>Seated Movement & Range-of-Motion Guide</p>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight mb-14">Gentle, low-impact movement — in rhythm.</h2>
          <MovementFlow steps={SEATED} accent={ACCENT} testid="pathways-movement-flow" />
        </div>
      </section>

      <section className="py-16 md:py-24 px-6 md:px-10 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <p className="font-mono text-xs tracking-[0.3em] uppercase mb-3" style={{ color: ACCENT }}>Comprehensive Impact</p>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight mb-14">What rhythm reaches for those navigating memory loss.</h2>
          <ImpactRings items={IMPACT} accent={ACCENT} testid="pathways-impact" />
        </div>
      </section>

      <section className="py-16 md:py-24 px-6 md:px-10 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <p className="font-mono text-xs tracking-[0.3em] uppercase mb-3" style={{ color: ACCENT }}>Beyond the Drum Circle</p>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight mb-12">Not a performance — a pathway.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="shape-r p-7 border border-white/10 bg-black/20" data-testid="pathways-assumption">
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-fg/40 mb-5">The old assumption</p>
              <ul className="space-y-3">
                {ASSUMPTION.map((a) => <li key={a} className="flex items-start gap-3 text-fg/55"><X size={17} className="mt-0.5 shrink-0 text-fg/40" /> {a}</li>)}
              </ul>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }} className="glass shape-r p-7" style={{ border: `1px solid ${ACCENT}55` }} data-testid="pathways-approach">
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase mb-5" style={{ color: ACCENT }}>The Pathways approach</p>
              <ul className="space-y-3">
                {PATHWAYS.map((a) => <li key={a} className="flex items-start gap-3"><Check size={17} className="mt-0.5 shrink-0" style={{ color: ACCENT }} /> {a}</li>)}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 border-t border-white/5 overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <p className="font-mono text-xs tracking-[0.3em] uppercase mb-3" style={{ color: ACCENT }}>Moments From Real Sessions</p>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight mb-3">Presence, one beat at a time.</h2>
          <p className="text-fg/55 text-sm mb-10 max-w-xl">Glimpses of clarity, joy, and reconnection from care communities we've served.</p>
        </div>
        <MotionGallery items={GALLERY} accent={ACCENT} testid="pathways-gallery" />
      </section>

      <section id="pathways-kit" className="py-16 md:py-24 px-6 md:px-10 border-t border-white/5">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-2 mb-3" style={{ color: ACCENT }}><FileDown size={16} /><p className="font-mono text-xs tracking-[0.3em] uppercase">Information Kit</p></div>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight mb-4">Printable kit for directors & boards.</h2>
          <p className="text-fg/60 md:text-lg mb-8">Everything you need for budget and board approval — outcomes, formats, and program overview.</p>
          <LeadForm
            testid="pathways-kit-form" type="download" page="percussion-pathways" accent={ACCENT}
            submitLabel="Send the kit" showMessage={false}
            selects={[{ name: "role", label: "Your role", options: ["Activity Coordinator", "Memory Care Director", "Physical/Occupational Therapist", "Family Caregiver", "Administrator"] }]}
            downloadUrl="/casey-percussion-pathways-kit.pdf"
            successTitle="Your info kit is ready." successBody="Download below — we've also emailed a copy for your records."
          />
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 border-t border-white/5 text-center">
        <h2 className="font-serif text-4xl md:text-6xl tracking-tight">Bring therapeutic rhythm to your space.</h2>
        <a href={SCHEDULER_URL} target="_blank" rel="noopener noreferrer" data-testid="pathways-book-bottom" className="inline-flex items-center gap-2 mt-8 shape-r font-medium px-8 py-4 transition-opacity hover:opacity-90" style={{ backgroundColor: ACCENT, color: "#141b1d" }}>Book a Session <ArrowRight size={17} /></a>
      </section>
      <Footer />
    </div>
  );
}
